import ApiService from "@/api";
import queryString from "query-string";
import axios from "axios";
import { API_MEWS, HOTEL_ID, CLIENT, ADULT_COUNT_DEFAULT } from "@/api/config";
import { map, get, find, pick, first } from "lodash";
import {
    formatBookDate,
    checkIsValidDatePayment
} from "@/helper/calculateDays";
import moment from "moment";
// initial state
const state = () => ({
    loading: false,
    references: [],
    availability: {},
    hotels: {},
    roomCategoryAvailabilities: [],
    filter: {},
    promotions: [],
    homePromotions: [],
    allReferences: []
});

// getters
const getters = {};

// actions
const actions = {
    getRoom({ commit }, roomCategory) {
        commit("setLoading", true);
        commit("resetRoom");
        return new Promise(resolve => {
            ApiService.get(`/get-product/?reference=${roomCategory.key}`)
                .then(({ data }) => {
                    commit("setRoom", {
                        data,
                        roomCategory
                    });
                    commit("setLoading", false);
                    resolve(data);
                })
                .catch(() => {
                    commit("setLoading", false);
                });
        });
    },
    getHotels({ commit }) {
        commit("setLoading", true);
        return new Promise(resolve => {
            axios
                .post(`${API_MEWS}/hotels/get`, {
                    Client: CLIENT,
                    HotelId: HOTEL_ID
                })
                .then(({ data }) => {
                    commit("setHotels", data);
                    commit("setLoading", false);
                    resolve(data);
                })
                .catch(() => {
                    commit("setLoading", false);
                });
        });
    },
    getPromotions(context) {
        const { commit, rootState } = context;
        commit("setLoading", true);
        let url = `/get-promotions`;
        if (get(rootState, "app.lang") === "en") {
            url = `/en/get-promotions`;
        }
        return new Promise(resolve => {
            ApiService.get(url)
                .then(({ data }) => {
                    if (data.length) {
                        const mapPromise = data
                            .filter(item =>
                                checkIsValidDatePayment(
                                    moment(item.start_date, "DD-MM-YYYY"),
                                    moment(item.end_date, "DD-MM-YYYY")
                                )
                            )
                            .map(item => {
                                const params = {
                                    StartUtc: formatBookDate(
                                        moment(item.start_date, "DD-MM-YYYY")
                                    ),
                                    EndUtc: formatBookDate(
                                        moment(item.end_date, "DD-MM-YYYY")
                                    ),
                                    VoucherCode: item.code,
                                    AdultCount: ADULT_COUNT_DEFAULT,
                                    ChildCount: 0
                                };
                                return new Promise((resolve, reject) => {
                                    axios
                                        .post(
                                            `${API_MEWS}/hotels/getAvailability`,
                                            {
                                                ...params,
                                                Client: CLIENT,
                                                HotelId: HOTEL_ID
                                            }
                                        )
                                        .then(({ data }) => {
                                            resolve({
                                                ...item,
                                                roomCategoryAvailabilities: data
                                            });
                                        })
                                        .catch(({ data }) => {
                                            reject(data);
                                        });
                                });
                            });
                        Promise.all(mapPromise).then(result => {
                            commit("setPromotions", result);
                            resolve(data);
                        });
                    }
                    commit("setLoading", false);
                })
                .catch(() => {
                    commit("setLoading", false);
                });
        });
    },
    getAllReferences(context) {
        const { commit, rootState } = context;
        let url = `/get-references`;
        if (get(rootState, "app.lang") === "en") {
            url = `/en/get-references`;
        }
        return new Promise(resolve => {
            ApiService.get(url)
                .then(({ data }) => {
                    commit("setAllReferences", get(data, "references", []));
                    resolve(data);
                })
                .catch(() => {});
        });
    },
    getReferences(context, data) {
        const { commit, rootState } = context;
        commit("setLoading", true);
        let dataReferences = {
            ...pick(data, ["camping", "categorie", "type"]),
            count: data.adult + data.child + data.child1
        };
        const stringified = queryString.stringify(dataReferences);
        commit("setFilter", data);
        let url = `/get-references/?${stringified}`;
        if (get(rootState, "app.lang") === "en") {
            url = `/en/get-references/?${stringified}`;
        }
        return new Promise(resolve => {
            ApiService.get(url)
                .then(response => {
                    const { data } = response;
                    commit("setReferences", data);
                    if (!data.references) {
                        commit("resetAvailability");
                    }
                    commit("setLoading", false);
                    resolve(data);
                })
                .catch(() => {
                    commit("setLoading", false);
                });
        });
    },
    getAvailability({ commit }, params) {
        commit("setLoading", true);
        return new Promise((resolve, reject) => {
            axios
                .post(`${API_MEWS}/hotels/getAvailability`, {
                    ...params,
                    Client: CLIENT,
                    HotelId: HOTEL_ID
                })
                .then(({ data }) => {
                    commit("setLoading", false);
                    commit("setAvailability", data);
                    resolve(data);
                })
                .catch(({ data }) => {
                    commit("setLoading", false);
                    reject(data);
                });
        });
    },
    getReservationGroup({ commit }, params) {
        return new Promise((resolve, reject) => {
            axios
                .post(`${API_MEWS}/reservationGroups/get`, {
                    ...params,
                    Client: CLIENT,
                    HotelId: HOTEL_ID
                })
                .then(({ data }) => {
                    commit("setLoading", false);
                    resolve(data);
                })
                .catch(({ data }) => {
                    reject(data);
                });
        });
    }
};

// mutations
const mutations = {
    setLoading(state, loading) {
        state.loading = loading;
    },
    setAllReferences(state, data) {
        state.allReferences = data;
    },
    setReferences(state, data) {
        state.references = data;
    },
    setAvailability(state, data) {
        state.availability = data;
        const { RoomCategoryAvailabilities } = data;
        const roomCategories = get(state, "hotels.RoomCategories", []);
        const roomCategoryAvailabilities = map(
            RoomCategoryAvailabilities,
            category => {
                const roomCategoryInformation = find(
                    roomCategories,
                    item => item.Id === category.RoomCategoryId
                );
                const reference = find(
                    get(state, "references.references", []),
                    item => item.id === category.RoomCategoryId
                );
                return {
                    // Id: category.RoomCategoryId,
                    // AvailableRoomCount: category.AvailableRoomCount,
                    ...category,
                    ...roomCategoryInformation,
                    ...pick(reference, ["camping"])
                };
            }
        );
        state.roomCategoryAvailabilities = roomCategoryAvailabilities;

        // re-update room data
        if (get(state, "room.Id")) {
            const roomCategoryAvailabilities = find(
                state.roomCategoryAvailabilities,
                item => item.RoomCategoryId === get(state, "room.Id")
            );
            const roomCategories = get(state, "hotels.RoomCategories", []);
            const roomCategoryInformation = find(
                roomCategories,
                item => item.Id === get(state, "room.Id")
            );
            state.room = {
                ...state.room,
                ...roomCategoryAvailabilities,
                ...roomCategoryInformation
            };
        }
    },
    resetAvailability(state) {
        state.availability = {};
        state.roomCategoryAvailabilities = [];
    },
    setHotels(state, data) {
        state.hotels = data;
    },
    setFilter(state, data) {
        state.filter = data;
    },
    resetRoom(state) {
        state.room = {};
    },
    setRoom(state, { data, roomCategory }) {
        const roomCategoryAvailabilities = find(
            state.roomCategoryAvailabilities,
            item => item.RoomCategoryId === roomCategory.id
        );
        const roomCategories = get(state, "hotels.RoomCategories", []);

        const roomCategoryInformation = find(
            roomCategories,
            item => item.Id === roomCategory.id
        );
        const room = {
            info: data,
            ...roomCategoryAvailabilities,
            ...roomCategoryInformation
        };
        state.room = room;
    },
    setPromotions(state, data) {
        data.sort(function compare(a, b) {
            var dateA = new Date(a.start_date);
            var dateB = new Date(b.start_date);
            return dateA - dateB;
        });
        const roomCategories = get(state, "hotels.RoomCategories", []);
        let newData = [];
        data.forEach(itemData => {
            let reference = [];
            map(itemData.reference, roomCategory => {
                const roomCategoryInformation = find(
                    roomCategories,
                    item => item.Id === roomCategory.id
                );
                let room = {};
                if (roomCategoryInformation) {
                    room = {
                        ...roomCategory,
                        ...roomCategoryInformation,
                        roomCategoryAvailabilities:
                            itemData.roomCategoryAvailabilities
                    };
                }
                if (Object.keys(room).length) {
                    reference.push(room);
                }
            });
            if (reference.length) {
                newData.push({
                    ...itemData,
                    reference
                });
            }
        });
        state.promotions = newData;
        let homePromotions = [];
        const firstPromotion = first(newData);
        homePromotions = get(first(newData), "reference", []).map(item => ({
            ...item,
            promotionStartDate: firstPromotion.start_date,
            promotionEndDate: firstPromotion.end_date,
            promotionValue: firstPromotion.value,
            promotionCode: firstPromotion.code
        }));
        state.homePromotions = homePromotions;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
